import { useState } from 'react';

// Shared dashboard charts (vendor + super-admin dashboards).

const nf = (n: number) => n.toLocaleString('en-IN');

const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

// 12-month lead trend — single series area + line, hover crosshair tooltip.
// format: how values render in axis ticks + tooltip (default: plain count + unit).
export function TrendChart({ data, unit = 'leads', label = 'New leads by month', format }: { data: { month: string; count: number }[]; unit?: string; label?: string; format?: (n: number) => string }) {
  const fmt = format ?? ((n: number) => `${nf(n)} ${unit}`);
  const [hover, setHover] = useState<number | null>(null);
  const W = 640, H = 200, PL = 36, PR = 12, PT = 12, PB = 26;
  const max = Math.max(1, ...data.map((d) => d.count));
  const niceMax = max <= 5 ? 5 : Math.ceil(max / 4) * 4;
  const x = (i: number) => PL + (data.length <= 1 ? 0 : (i * (W - PL - PR)) / (data.length - 1));
  const y = (v: number) => PT + (H - PT - PB) * (1 - v / niceMax);
  const line = data.map((d, i) => `${i ? 'L' : 'M'}${x(i)},${y(d.count)}`).join(' ');
  const area = `${line} L${x(data.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;
  const ticks = [0, niceMax / 2, niceMax];

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={label}>
        <defs>
          <linearGradient id="dash-trend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366F1" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
          </linearGradient>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PL} x2={W - PR} y1={y(t)} y2={y(t)} stroke="#E2E8F0" strokeDasharray="3 4" />
            <text x={PL - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill="#94A3B8">{format ? format(Math.round(t)) : nf(Math.round(t))}</text>
          </g>
        ))}
        <path d={area} fill="url(#dash-trend-fill)" />
        <path d={line} fill="none" stroke="#6366F1" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null && (
          <line x1={x(hover)} x2={x(hover)} y1={PT} y2={y(0)} stroke="#CBD5E1" />
        )}
        {data.map((d, i) => (
          <circle key={d.month} cx={x(i)} cy={y(d.count)} r={hover === i ? 5 : 3.5} fill="#fff" stroke="#6366F1" strokeWidth="2" />
        ))}
        {data.map((d, i) => (i % 2 === 0 || i === data.length - 1) && (
          <text key={`l-${d.month}`} x={x(i)} y={H - 6} textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'} fontSize="10" fill="#94A3B8">
            {monthLabel(d.month)}
          </text>
        ))}
        {/* Hit areas wider than the marks */}
        {data.map((d, i) => (
          <rect
            key={`h-${d.month}`}
            x={x(i) - (W - PL - PR) / (2 * Math.max(1, data.length - 1))}
            y={PT}
            width={(W - PL - PR) / Math.max(1, data.length - 1)}
            height={H - PT - PB}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </svg>
      {hover !== null && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-md"
          style={{ left: `${(x(hover) / W) * 100}%`, top: 0 }}
        >
          <p className="text-slate-500">{monthLabel(data[hover].month)}</p>
          <p className="font-semibold text-slate-900">{fmt(data[hover].count)}</p>
        </div>
      )}
    </div>
  );
}

// Pipeline donut — status colors come from the tenant's status master.
export function Donut({ items, total, unit = 'leads' }: { items: { name: string; color: string; count: number }[]; total: number; unit?: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const R = 52, C = 2 * Math.PI * R, GAP = 2;
  let offset = 0;
  return (
    <div className="relative h-[150px] w-[150px] flex-shrink-0">
      <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90" role="img" aria-label={`${unit} by status`}>
        <circle cx="70" cy="70" r={R} fill="none" stroke="#F1F5F9" strokeWidth="16" />
        {items.map((it, i) => {
          const len = total > 0 ? (it.count / total) * C : 0;
          const seg = (
            <circle
              key={it.name}
              cx="70" cy="70" r={R} fill="none"
              stroke={it.color}
              strokeWidth={hover === i ? 19 : 16}
              strokeDasharray={`${Math.max(0, len - (items.length > 1 ? GAP : 0))} ${C}`}
              strokeDashoffset={-offset}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              className="transition-[stroke-width]"
            />
          );
          offset += len;
          return seg;
        })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        {hover !== null ? (
          <>
            <span className="text-lg font-bold text-slate-900">{nf(items[hover].count)}</span>
            <span className="max-w-[90px] truncate text-[10px] text-slate-500">{items[hover].name}</span>
          </>
        ) : (
          <>
            <span className="text-xl font-bold text-slate-900">{nf(total)}</span>
            <span className="text-[10px] text-slate-500">{unit}</span>
          </>
        )}
      </div>
    </div>
  );
}

